/*
 * nf-lp-atc.js — nail-freedom landing page interactivity (page-scoped via layout guard).
 * ONE file, delegated listeners (no per-card inline JS):
 *   1. AJAX Add-to-Cart  — /cart/add.js, updates the theme's own cart, never redirects.
 *   2. Sticky bundle bar — persistent state: count / free-set rule / subtotal / state-changing CTA.
 *   3. In-grid selected state — tiles that are already in the cart look different and stay "Added ✓".
 *
 * WHY THE STICKY BAR (2026-07-25, replaces the scroll-away #nf-progress pill):
 *   Clarity paid-cohort forensics (reports/meta-ads-analysis/clarity-cohort-analysis-20260725.md)
 *   found the offer needs 5 items while the average adder stopped at 3.25, and that suppressing the
 *   drawer also suppressed the only surface carrying the discount line, the delivery ETA and the
 *   checkout button. The old pill sat ABOVE the grid and scrolled out of view by roughly the third
 *   add, so the "how far is left" signal — the thing the endowed-progress effect needs to work —
 *   was gone exactly when it mattered. The bar keeps that state visible WITHOUT interrupting serial
 *   adding: no forced drawer, nothing covering the grid, nothing to dismiss.
 *
 * Drawer policy: never on add #1-#2. ONE dismissible auto-open on add #3 (the moment discount logic
 * and ETA become decision-useful), then user-invoked only. Serial adding stays uninterrupted.
 *
 * rev marker (deploy idempotency): sticky-bundle-bar i18n rev-20260725h offer-not-code
 */
var NF_LP_RUNTIME = {
  adding: 'Adding…', soldOut: 'Sold out', tryAgain: 'Try again', addToCart: 'Add to Cart',
  etaOffer: '', etaBasis: '', etaToday: ''
};
try {
  var nfRuntimeNode = document.getElementById('nf-lp-runtime-i18n');
  if (nfRuntimeNode) {
    var nfRuntimeParsed = JSON.parse(nfRuntimeNode.textContent || '{}');
    Object.keys(NF_LP_RUNTIME).forEach(function (key) {
      if (typeof nfRuntimeParsed[key] === 'string' && nfRuntimeParsed[key]) NF_LP_RUNTIME[key] = nfRuntimeParsed[key];
    });
  }
} catch (e) {}

(function () {
  var GOAL_FALLBACK = 5;

  // ---- i18n ----------------------------------------------------------------
  // The bar's live state text is generated here, so the page-body translation pipeline (which only
  // touches body_html) can never reach it. The ladder strings (pre/one/many/suf/chooseH/chooseS/
  // unlockH/unlockS/repeat/added/more/checkout) are LIFTED VERBATIM from the theme's own reviewed
  // `b4g1_ladder` locale blocks — the same ones the cart drawer renders — so the two surfaces agree
  // word for word in every language.
  //
  // EXCEPTION — `idle` (2026-07-25): deliberately NO LONGER verbatim. Two changes, both intentional.
  //
  //   1. The reviewed b4g1_ladder wording ended with "no code needed". True of B4G1F (an automatic
  //      order discount), but it sat on the one permanently-visible element and read as a blanket
  //      "there are no codes here" — suppressing the stacking subscription 10%
  //      (combinesWith.productDiscounts = true, read back from the Discount API 2026-07-25).
  //      Stacked, the two are ~27% off a five-set basket. `idle` now names both offers.
  //
  //   2. It names the OFFER, never the CODE. Printing "WELCOME10" here would defeat the gated
  //      email capture that PDP / cart / cart-drawer implement (email in -> code revealed +
  //      Omnisend identifyContact). Verified 2026-07-25: on a fresh PDP session all 7 WELCOME10
  //      nodes render with offsetParent null — the shopper sees only the email field. Any future
  //      edit that prints the literal code on a public surface silently kills that capture path.
  //      The one place it leaked was the landing-page body (introduced 8be7af0a, 2026-07-03);
  //      that leak was closed in the same change as this comment.
  //
  // These strings ARE newly written per language and want native review; the rest stay verbatim.
  //
  // The CTA is deliberately language-neutral ("+2") with a localised aria-label: the reviewed
  // `shop_more` strings run up to 30 characters ("Einen weiteren Style hinzufügen"), which overflows a
  // sticky mobile button, and inventing shorter microcopy in five languages is exactly the kind of
  // guesswork that produces awkward text. The words live in the two translated lines above the button.
  var STR = {
    en: { head:'Pick any 5. Pay for 4.', idle:'Lowest-priced set free · Subscribe: 10% · Review: 10% next order', repeat:'Repeats every 5 sets.',
          pre:'Pick', one:'more style', many:'more styles', suf:'— the lowest-priced set is free.',
          chooseH:'Pick your 5th style. Pay for 4.', chooseS:'The lowest-priced eligible set is free at checkout.',
          unlockH:'Your 5-set wardrobe is ready. You pay for 4.', unlockS:'The lowest-priced eligible set is free at checkout.',
          added:'Added ✓', more:'Add another style', checkout:'Checkout' },
    fr: { head:'Choisissez-en 5. Payez-en 4.', idle:'Le moins cher offert · Abonnement : −10 % · Avis : −10 % prochaine commande', repeat:'L’offre se répète tous les 5 sets.',
          pre:'Choisissez encore', one:'modèle', many:'modèles', suf:'— le set le moins cher est offert.',
          chooseH:'Choisissez votre 5e modèle. Payez-en 4.', chooseS:'Le set éligible le moins cher est offert au paiement.',
          unlockH:'Vos 5 modèles sont prêts. Vous en payez 4.', unlockS:'Le set éligible le moins cher sera offert au paiement.',
          added:'Ajouté ✓', more:'Ajouter un autre modèle', checkout:'Commander' },
    de: { head:'Wählen Sie 5 beliebige Styles. Bezahlen Sie 4.', idle:'Günstigstes Set gratis · E-Mail-Abo: 10 % · Bewertung: 10 % nächste Bestellung', repeat:'Gilt für jeweils 5 Sets.',
          pre:'Wählen Sie noch', one:'Style', many:'Styles', suf:'— das günstigste Set ist gratis.',
          chooseH:'Wählen Sie Ihren 5. Style. Bezahlen Sie 4.', chooseS:'Das günstigste teilnehmende Set ist an der Kasse gratis.',
          unlockH:'Ihre 5 Styles sind komplett. Sie bezahlen 4.', unlockS:'Das günstigste teilnehmende Set ist an der Kasse gratis.',
          added:'Hinzugefügt ✓', more:'Einen weiteren Style hinzufügen', checkout:'Zur Kasse' },
    es: { head:'Elige 5 cualesquiera. Paga 4.', idle:'El de menor precio gratis · Suscríbete: 10 % · Reseña: 10 % próximo pedido', repeat:'Se aplica por cada 5 sets.',
          pre:'Elige', one:'estilo más', many:'estilos más', suf:'— el de menor precio es gratis.',
          chooseH:'Elige tu quinto estilo. Paga 4.', chooseS:'El set elegible de menor precio es gratis al pagar.',
          unlockH:'Tus 5 estilos están listos. Pagas 4.', unlockS:'El set elegible de menor precio será gratis al pagar.',
          added:'Añadido ✓', more:'Añadir otro estilo', checkout:'Pagar' },
    it: { head:'Scegline 5. Pagane 4.', idle:'Il meno caro gratis · Iscriviti: 10% · Recensione: 10% prossimo ordine', repeat:'Si ripete ogni 5 set.',
          pre:'Scegli ancora', one:'stile', many:'stili', suf:'— il set meno caro è gratis.',
          chooseH:'Scegli il 5° stile. Pagane 4.', chooseS:'Il set idoneo meno caro è gratis al checkout.',
          unlockH:'I tuoi 5 stili sono pronti. Ne paghi 4.', unlockS:'Il set idoneo meno caro sarà gratis al checkout.',
          added:'Aggiunto ✓', more:'Aggiungi un altro stile', checkout:'Vai al checkout' },
    ja: { head:'お好きな5点を選び、4点分だけお支払い。', idle:'最安の1点が無料・メール購読10%オフ・レビューで次回10%オフ', repeat:'5点ごとに繰り返し適用されます。',
          pre:'あと', one:'スタイル', many:'スタイル', suf:'選ぶと、最も価格の低いセットが無料。',
          chooseH:'5点目を選び、4点分だけお支払い。', chooseS:'チェックアウト時に対象商品のうち最も価格の低い1点が無料になります。',
          unlockH:'5点揃いました。4点分だけお支払い。', unlockS:'チェックアウト時に対象商品のうち最も価格の低い1点が無料になります。',
          added:'追加済み ✓', more:'別のスタイルを追加する', checkout:'レジへ進む' },
    pt: { head:'Escolha 5. Pague 4.', idle:'O de menor preço grátis · Assine: 10% · Avalie: 10% no próximo pedido', repeat:'Repete-se a cada 5 kits.',
          pre:'Escolha mais', one:'estilo', many:'estilos', suf:'— o kit de menor preço é grátis.',
          chooseH:'Escolha o 5º estilo. Pague 4.', chooseS:'O kit elegível de menor preço é grátis ao finalizar a compra.',
          unlockH:'Seus 5 estilos estão prontos. Você paga 4.', unlockS:'O kit elegível de menor preço será grátis ao finalizar a compra.',
          added:'Adicionado ✓', more:'Adicione outro estilo', checkout:'Finalizar compra' },
    ko: { head:'원하는 5개를 고르고 4개만 결제하세요.', idle:'최저가 1개 무료 · 이메일 구독 10% 할인 · 리뷰 시 다음 주문 10% 할인', repeat:'5개마다 반복 적용됩니다.',
          pre:'추가로', one:'개 스타일을', many:'개 스타일을', suf:'더 고르면 가장 저렴한 세트가 무료입니다.',
          chooseH:'5번째 스타일을 고르고 4개만 결제하세요.', chooseS:'결제 시 적용 대상 중 가장 저렴한 세트가 무료입니다.',
          unlockH:'5개를 모두 골랐습니다. 4개만 결제하세요.', unlockS:'결제 시 적용 대상 중 가장 저렴한 세트가 무료입니다.',
          added:'추가됨 ✓', more:'다른 스타일 추가', checkout:'결제하기' },
    id: { head:'Pilih 5. Bayar 4.', idle:'Yang termurah gratis · Langganan: 10% · Ulasan: diskon 10% pesanan berikutnya', repeat:'Berlaku lagi untuk setiap 5 set.',
          pre:'Pilih', one:'gaya lagi', many:'gaya lagi', suf:'— set termurah gratis.',
          chooseH:'Pilih gaya ke-5. Bayar 4.', chooseS:'Set memenuhi syarat dengan harga terendah gratis saat checkout.',
          unlockH:'Pilihan 5 set Anda siap. Bayar 4.', unlockS:'Set memenuhi syarat dengan harga terendah gratis saat checkout.',
          added:'Ditambahkan ✓', more:'Tambah gaya lain', checkout:'Checkout' }
  };
  function T() {
    var lang = (document.documentElement.getAttribute('lang') || 'en').toLowerCase().split('-')[0];
    return STR[lang] || STR.en;
  }

  function store(name) {
    try { return (window.Alpine && Alpine.store) ? Alpine.store(name) : null; } catch (e) { return null; }
  }
  function sectionInner(html, selector) {
    try {
      var t = document.createElement('template'); t.innerHTML = html;
      var el = t.content.querySelector(selector); return el ? el.innerHTML : html;
    } catch (e) { return html; }
  }
  function findDrawerHost() {
    var cur = document.getElementById('CartDrawer');
    if (cur && cur.parentElement) return cur.parentElement;
    return Array.prototype.slice.call(document.querySelectorAll('div')).filter(function (el) {
      var h = el.getAttribute('@init-run.window'); return h && h.indexOf("'cart-drawer'") > -1;
    })[0];
  }
  function sectionsList() {
    var s = store('xCartHelper');
    if (s && typeof s.getSectionsToRender === 'function') { try { return s.getSectionsToRender(); } catch (e) {} }
    return [
      { id: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      { id: 'mobile-cart-icon-bubble', selector: '#mobile-cart-icon-bubble' },
      { id: 'cart-drawer', selector: '#CartDrawer' }
    ];
  }
  function setBtn(btn, txt, disabled) {
    btn.textContent = txt;
    if (disabled) btn.setAttribute('disabled', 'disabled'); else btn.removeAttribute('disabled');
  }
  function openDrawer() {
    try {
      var mc = store('xMiniCart');
      if (mc && typeof mc.openCart === 'function') { mc.openCart(); return true; }
      var icon = document.getElementById('cart-icon');
      if (icon) { icon.click(); return true; }
    } catch (e) {}
    return false;
  }

  // ---- cart state -----------------------------------------------------------
  // The cart bubble is the cheapest count source and is re-rendered by every add.
  function cartCount() {
    var span = document.querySelector('#cart-icon-bubble span');
    var n = span ? parseInt((span.textContent || '').replace(/[^0-9]/g, ''), 10) : 0;
    return isNaN(n) ? 0 : n;
  }
  // Subtotal/lines need the real cart. Cached so the bar can render before the fetch lands.
  var CART = { total: 0, prices: [], ids: {} };
  function money(cents) {
    var v = (cents || 0) / 100;
    return '$' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }
  function readCart(then) {
    fetch('/cart.js', { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        CART.total = c.total_price || 0;
        CART.prices = []; CART.ids = {};
        (c.items || []).forEach(function (it) {
          CART.ids[String(it.variant_id)] = (CART.ids[String(it.variant_id)] || 0) + (it.quantity || 1);
          for (var k = 0; k < (it.quantity || 1); k++) CART.prices.push(it.price || 0);
        });
        CART.prices.sort(function (a, b) { return a - b; });
        if (then) then();
      })
      .catch(function () { if (then) then(); });
  }

  // ---- in-grid selected state ----------------------------------------------
  // Baymard: most sites fail to distinguish items already in the cart, so shoppers lose track of
  // what they picked. Driven off the real cart so it survives a reload, not just this JS session.
  function paintSelected() {
    var scope = document.getElementById('nf-lp'); if (!scope) return;
    Array.prototype.forEach.call(scope.querySelectorAll('.nf-pcard'), function (card) {
      var idEl = card.querySelector('form.nf-atcform input[name=id]');
      var btn = card.querySelector('.nf-atc');
      var inCart = idEl && CART.ids[String(idEl.value)];
      if (inCart) {
        card.classList.add('is-sel');
        if (btn && !btn.dataset.busy) setBtn(btn, T().added, false);
      } else {
        card.classList.remove('is-sel');
        if (btn && !btn.dataset.busy && btn.textContent === T().added) setBtn(btn, btn.dataset.label || NF_LP_RUNTIME.addToCart, false);
      }
    });
  }

  // ---- sticky bundle bar ----------------------------------------------------
  function bar() { return document.getElementById('nf-bar'); }
  function updateBar() {
    var b = bar(); if (!b) return;
    var goal = parseInt(b.getAttribute('data-goal'), 10) || GOAL_FALLBACK;
    var n = cartCount();
    var l1 = document.getElementById('nf-bar-l1');
    var l2 = document.getElementById('nf-bar-l2');
    var cta = document.getElementById('nf-bar-cta');
    var trk = document.getElementById('nf-bar-track');
    if (!l1 || !l2 || !cta) return;

    if (trk) trk.style.width = Math.min(100, Math.round(n / goal * 100)) + '%';
    b.classList.toggle('done', n >= goal);
    b.setAttribute('data-count', String(n));

    var sub = CART.total;
    var freeAmt = (n >= goal && CART.prices.length) ? CART.prices[0] : 0;

    // Copy deliberately reuses the theme's existing `b4g1_ladder` locale wording (the same ladder the
    // cart drawer renders): "Build your 5-set wardrobe" / "Add N more eligible sets to unlock Buy 4
    // Get 1 Free." / "Choose your 5th style." / "Your wardrobe offer is unlocked." Two different
    // phrasings for one offer on one journey would be the same defect class as the two delivery
    // windows. Note the ladder's own 4/5 action is "add another style", not "review your cart" — so
    // the CTA at goal-1 sends the shopper back to the grid rather than opening the drawer.
    var t = T();
    var left = goal - n;
    function needLine() {
      var unit = (left === 1) ? t.one : t.many;
      return (t.pre ? t.pre + ' ' : '') + left + ' ' + unit + ' ' + t.suf;
    }
    if (n <= 0) {
      l1.textContent = t.head;
      l2.textContent = t.idle;
      cta.textContent = '+' + goal;
    } else if (n < goal - 1) {
      l1.textContent = t.head + ' (' + n + '/' + goal + ') · ' + money(sub);
      l2.textContent = needLine();
      cta.textContent = '+' + left;
    } else if (n === goal - 1) {
      l1.textContent = t.chooseH + ' · ' + money(sub);
      l2.textContent = t.chooseS;
      cta.textContent = '+1';
    } else {
      l1.textContent = t.unlockH + ' · ' + money(sub) + (freeAmt ? ' (−' + money(freeAmt) + ')' : '');
      l2.textContent = t.unlockS;
      l2.textContent += ' · ' + t.repeat;
      cta.textContent = t.checkout;
      cta.setAttribute('data-act', 'checkout');
      cta.setAttribute('aria-label', t.checkout);
      return;
    }
    l2.textContent += ' · ' + t.repeat;
    cta.setAttribute('data-act', 'scroll');
    cta.setAttribute('aria-label', t.more);   // full reviewed wording for screen readers
  }
  function refresh() { readCart(function () { updateBar(); paintSelected(); }); updateBar(); }

  document.addEventListener('click', function (ev) {
    var cta = ev.target && ev.target.closest ? ev.target.closest('#nf-bar-cta') : null;
    if (!cta) return;
    ev.preventDefault();
    var act = cta.getAttribute('data-act');
    if (act === 'checkout') { window.location.href = '/checkout'; return; }
    if (act === 'drawer') { openDrawer(); return; }
    var grid = document.getElementById('nf-grid');
    if (grid) { try { grid.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) { grid.scrollIntoView(); } }
  }, false);

  // ---- toast ---------------------------------------------------------------
  var toastT;
  function toast(msg) {
    var t = document.getElementById('nf-toast'); if (!t) return;
    t.textContent = msg; t.classList.add('on');
    clearTimeout(toastT); toastT = setTimeout(function () { t.classList.remove('on'); }, 1500);
  }

  // ---- AJAX add-to-cart ----------------------------------------------------
  var drawerShown = false;   // ONE dismissible auto-open, on add #3 only
  document.addEventListener('submit', function (ev) {
    var form = ev.target && ev.target.closest ? ev.target.closest('form.nf-atcform') : null;
    var scope = document.getElementById('nf-lp');
    if (!form || !scope || !scope.contains(form)) return;
    ev.preventDefault();

    var btn = form.querySelector('.nf-atc');
    if (!btn || btn.dataset.busy) return;
    btn.dataset.busy = '1';
    var orig = btn.dataset.label || btn.textContent; btn.dataset.label = orig;
    setBtn(btn, NF_LP_RUNTIME.adding, true);

    var idEl = form.querySelector('input[name=id]');
    var id = idEl ? Number(idEl.value) : 0;
    var secs = sectionsList();

    fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }], sections: secs.map(function (s) { return s.id; }), sections_url: window.location.pathname })
    }).then(function (r) { return r.json().then(function (j) { return { status: r.status, j: j }; }); })
    .then(function (res) {
      var resp = res.j;
      if (res.status === 422) { setBtn(btn, NF_LP_RUNTIME.soldOut, true); delete btn.dataset.busy; return; }
      if (res.status >= 400 || (resp && resp.status && Number(resp.status) >= 400)) throw new Error('add failed');
      try {
        secs.forEach(function (sec) {
          var el = document.querySelector(sec.selector);
          if (el && resp.sections && resp.sections[sec.id] != null) el.innerHTML = sectionInner(resp.sections[sec.id], sec.selector);
        });
      } catch (e) {}
      try {
        if (!document.getElementById('CartDrawer') && resp.sections && resp.sections['cart-drawer'] != null) {
          var host = findDrawerHost();
          if (host) { host.innerHTML = sectionInner(resp.sections['cart-drawer'], '#ajax-loading-cart'); var d = host.querySelector('#CartDrawer'); if (d && window.Alpine && Alpine.initTree) Alpine.initTree(d); }
        }
      } catch (e) {}
      try { var helper = store('xCartHelper'); if (helper) helper.currentItemCount = cartCount(); } catch (e) {}
      try { document.dispatchEvent(new CustomEvent('eurus:cart:items-changed')); } catch (e) {}

      delete btn.dataset.busy;
      setBtn(btn, T().added, false);   // stays localised "Added ✓" — the tile is now a selected tile
      var b = bar();
      var goal = b ? (parseInt(b.getAttribute('data-goal'), 10) || GOAL_FALLBACK) : GOAL_FALLBACK;
      var n = cartCount();
      // Language-neutral by design: a count needs no translation, and the localised state text
      // is already on the bar two lines above.
      toast('✓ ' + n + '/' + goal);
      refresh();

      // Add #3: surface discount logic + ETA + checkout once, dismissible. Then never again.
      if (!drawerShown && n === 3 && n < goal) { drawerShown = true; setTimeout(openDrawer, 450); }
    }).catch(function () {
      setBtn(btn, NF_LP_RUNTIME.tryAgain, false); delete btn.dataset.busy;
      setTimeout(function () { if (btn.textContent === NF_LP_RUNTIME.tryAgain) setBtn(btn, orig, false); }, 1800);
    });
  }, false);

  // Cart can change outside this page (drawer remove, quantity edit) — keep the bar honest.
  document.addEventListener('eurus:cart:items-changed', function () { setTimeout(refresh, 250); }, false);

  // ---- init ----------------------------------------------------------------
  function init() { if (document.getElementById('nf-lp')) refresh(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
  /* NF ETA uses the existing PDP Liquid calculator for the U.S. arrival window.
   * It follows the same market override and dispatch shift on each page load. */
  (function () {
    // Shopify Liquid supplies the U.S. dates; the browser only formats them.
    function pageLocale() {
      return (document.documentElement.getAttribute('lang') || 'en').replace('_', '-');
    }
    function formatWindow(minISO, maxISO) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(minISO) || !/^\d{4}-\d{2}-\d{2}$/.test(maxISO)) return '';
      var a = new Date(minISO + 'T00:00:00Z'), b = new Date(maxISO + 'T00:00:00Z');
      if (isNaN(a.getTime()) || isNaN(b.getTime()) || a > b ||
          a.toISOString().slice(0, 10) !== minISO || b.toISOString().slice(0, 10) !== maxISO) return '';
      var locale = pageLocale();
      var monthDay = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'short', day: 'numeric' });
      if (/^en(?:-|$)/i.test(locale) && a.getUTCFullYear() === b.getUTCFullYear() &&
          a.getUTCMonth() === b.getUTCMonth()) {
        var dayOnly = new Intl.DateTimeFormat(locale, { timeZone: 'UTC', day: 'numeric' });
        return monthDay.format(a) + '–' + dayOnly.format(b);
      }
      return monthDay.format(a) + ' – ' + monthDay.format(b);
    }
    function windowText() {
      var node = document.getElementById('nf-lp-eta-source');
      if (!node) return '';
      try {
        var data = JSON.parse(node.textContent || '{}');
        if (data.market !== 'US' || data.basisMin !== 9 || data.basisMax !== 14 ||
            !Number.isInteger(data.holidayShift) || data.holidayShift < 0 || data.holidayShift > 7 ||
            typeof data.minISO !== 'string' || typeof data.maxISO !== 'string') return '';
        return formatWindow(data.minISO, data.maxISO);
      } catch (e) { return ''; }
    }

    var ETA_TEMPLATE_KEYS = {
      '5 for 4 + 10% off, once per customer · arrives {window}': 'etaOffer',
      '{window} for mainland U.S. orders — based on 10–14 calendar days after order.': 'etaBasis',
      'Order today and it arrives {window} for mainland U.S. orders.': 'etaToday'
    };

    function runtimeKeyFor(node) {
      if (node.matches('.nf-offer2')) return 'etaOffer';
      if (node.closest('.nf-tr')) return 'etaBasis';
      if (node.matches('.nf-offerline')) return 'etaToday';
      return ETA_TEMPLATE_KEYS[node.getAttribute('data-nf-eta')];
    }

    function renderEta() {
      var nodes = document.querySelectorAll('#nf-lp [data-nf-eta]');
      if (!nodes.length) return;
      var w = windowText();
      var runtime = typeof NF_LP_RUNTIME === 'object' && NF_LP_RUNTIME ? NF_LP_RUNTIME : null;
      if (!w || !runtime || !['etaOffer', 'etaBasis', 'etaToday'].every(function (key) {
        return typeof runtime[key] === 'string' && runtime[key].indexOf('{window}') >= 0;
      })) {
        // No trustworthy source means no stale Page fallback date may remain visible.
        for (var j = 0; j < nodes.length; j++) {
          nodes[j].hidden = true;
          nodes[j].style.setProperty('display', 'none', 'important');
        }
        return;
      }
      for (var i = 0; i < nodes.length; i++) {
        var sourceTemplate = nodes[i].getAttribute('data-nf-eta');
        var runtimeKey = runtimeKeyFor(nodes[i]);
        var localizedTemplate = runtimeKey ? runtime[runtimeKey] : null;
        if (!localizedTemplate) {
          nodes[i].hidden = true;
          nodes[i].style.setProperty('display', 'none', 'important');
          continue;
        }
        nodes[i].textContent = localizedTemplate.replace('{window}', w);
        nodes[i].setAttribute('data-nf-eta-rendered', w);
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', renderEta);
    } else {
      renderEta();
    }
  })();
